import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="抱歉，您访问的文章不存在。" />
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="px-4 md:px-6 max-w-6xl mx-auto text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = getRelatedArticles(article.slug, article.categorySlug, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://pmt-pbt.cn/article/${article.slug}`,
    },
    publisher: {
      '@type': 'Organization',
      name: '2048核基地',
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://pmt-pbt.cn/article/${article.slug}`}
        ogType="article"
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-20 pb-20">
        <article className="px-4 md:px-6 max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || article.categorySlug, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="text-xs px-3 py-1 bg-primary-100 text-primary-700 rounded-md font-medium whitespace-nowrap hover:bg-primary-200 transition-colors"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">作者：{article.author}</span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none mb-12"
            style={{
              '--tw-prose-body': 'oklch(var(--foreground-700))',
              '--tw-prose-headings': 'oklch(var(--foreground-900))',
              '--tw-prose-links': 'oklch(var(--primary-600))',
              '--tw-prose-bold': 'oklch(var(--foreground-900))',
              '--tw-prose-quotes': 'oklch(var(--foreground-600))',
              '--tw-prose-quote-borders': 'oklch(var(--primary-400))',
            } as React.CSSProperties}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex items-center gap-2 flex-wrap mb-10 pt-6 border-t border-background-200">
            <span className="text-xs text-foreground-500 whitespace-nowrap">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-md whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <section className="pt-8 border-t border-background-200">
              <h2 className="text-xl font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {related.title}
                      </h3>
                      <span className="text-xs text-foreground-400">{related.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}